import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Menu,
  X,
  ChevronDown,
  HelpCircle,
  BookOpen,
  Gift,
  Home,
  Building,
  Shield,
  Car,
  Waves,
  Users,
  Factory,
  Eye,
  Settings,
  Zap,
  TreePine,
  Hammer,
  Sun,
  Grid3X3,
  ArrowRight
} from 'lucide-react';
import { openWhatsAppQuote } from '../utils/whatsapp';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isGrillsDropdownOpen, setIsGrillsDropdownOpen] = useState(false);
  const [isNetsDropdownOpen, setIsNetsDropdownOpen] = useState(false);
  const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
  const [isMobileGrillsOpen, setIsMobileGrillsOpen] = useState(false);
  const [isMobileNetsOpen, setIsMobileNetsOpen] = useState(false);
  // Navbar state management for separate Grills and Nets dropdowns
  const grillsDropdownRef = useRef<HTMLDivElement>(null);
  const netsDropdownRef = useRef<HTMLDivElement>(null);
  const moreDropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const toggleGrillsDropdown = () => {
    setIsGrillsDropdownOpen(!isGrillsDropdownOpen);
    setIsNetsDropdownOpen(false); // Close other dropdown
    setIsMoreDropdownOpen(false); // Close other dropdown
  };

  const toggleNetsDropdown = () => {
    setIsNetsDropdownOpen(!isNetsDropdownOpen);
    setIsGrillsDropdownOpen(false); // Close other dropdown
    setIsMoreDropdownOpen(false); // Close other dropdown
  };

  const toggleMoreDropdown = () => {
    setIsMoreDropdownOpen(!isMoreDropdownOpen);
    setIsGrillsDropdownOpen(false); // Close other dropdown
    setIsNetsDropdownOpen(false); // Close other dropdown
  };

  const toggleMobileGrills = () => {
    setIsMobileGrillsOpen(!isMobileGrillsOpen);
    setIsMobileNetsOpen(false); // Close other dropdown
  };

  const toggleMobileNets = () => {
    setIsMobileNetsOpen(!isMobileNetsOpen);
    setIsMobileGrillsOpen(false); // Close other dropdown
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (grillsDropdownRef.current && !grillsDropdownRef.current.contains(event.target as Node)) {
        setIsGrillsDropdownOpen(false);
      }
      if (netsDropdownRef.current && !netsDropdownRef.current.contains(event.target as Node)) {
        setIsNetsDropdownOpen(false);
      }
      if (moreDropdownRef.current && !moreDropdownRef.current.contains(event.target as Node)) {
        setIsMoreDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Hide navbar on landing page for mobile
  if (location.pathname === '/landing') {
    return null;
  }

  return (
    <nav className="bg-white shadow-md fixed w-full z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center">
            <img 
              src="/images/jaranets-logo.png" 
              alt="Jara Safety Nets" 
              className="h-14 w-auto"
            />
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-gray-600 hover:text-yellow-600 transition-colors">Home</Link>
            
            {/* Grills Dropdown */}
            <div className="relative" ref={grillsDropdownRef}>
              <button
                onClick={toggleGrillsDropdown}
                className="flex items-center space-x-1 text-gray-600 hover:text-yellow-600 transition-colors"
              >
                <span>Grills</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${isGrillsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isGrillsDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.12, ease: "easeOut" }}
                  className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-xl border border-gray-200/60 py-2 z-50"
                >
                  <div className="px-3 py-1">
                    {/* 1. Balcony Grills */}
                    <Link
                      to="/invisible-grill-balconies"
                      className="flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-md transition-colors"
                      onClick={() => setIsGrillsDropdownOpen(false)}
                    >
                      <span>Balcony Grills</span>
                      <ArrowRight className="h-3 w-3 text-gray-400" />
                    </Link>
                    
                    {/* 2. Window Grills */}
                    <Link
                      to="/invisible-grill-windows"
                      className="flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-md transition-colors"
                      onClick={() => setIsGrillsDropdownOpen(false)}
                    >
                      <span>Window Grills</span>
                      <ArrowRight className="h-3 w-3 text-gray-400" />
                    </Link>
                    
                    {/* 3. Apartment Grills */}
                    <Link
                      to="/invisible-grill-apartments"
                      className="flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-md transition-colors"
                      onClick={() => setIsGrillsDropdownOpen(false)}
                    >
                      <span>Apartment Grills</span>
                      <ArrowRight className="h-3 w-3 text-gray-400" />
                    </Link>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Nets Dropdown */}
            <div className="relative" ref={netsDropdownRef}>
              <button
                onClick={toggleNetsDropdown}
                className="flex items-center space-x-1 text-gray-600 hover:text-yellow-600 transition-colors"
              >
                <span>Nets</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${isNetsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isNetsDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.12, ease: "easeOut" }}
                  className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-xl border border-gray-200/60 py-2 z-50"
                >
                  <div className="px-3 py-1">
                    <Link
                      to="/products"
                      className="flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-900 hover:bg-blue-50 hover:text-blue-700 rounded-md transition-colors"
                      onClick={() => setIsNetsDropdownOpen(false)}
                    >
                      <span>All Products</span>
                      <ArrowRight className="h-3 w-3 text-gray-400" />
                    </Link>
                    
                    {/* 1. Anti Bird Net (Pigeon Nets) */}
                    <Link
                      to="/pigeon-nets"
                      className="flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-md transition-colors"
                      onClick={() => setIsNetsDropdownOpen(false)}
                    >
                      <span>Anti Bird Net</span>
                      <ArrowRight className="h-3 w-3 text-gray-400" />
                    </Link>
                    
                    {/* 2. Cricket Practice Net (Sports Safety) */}
                    <Link
                      to="/sports-safety"
                      className="flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-md transition-colors"
                      onClick={() => setIsNetsDropdownOpen(false)}
                    >
                      <span>Cricket Practice Net</span>
                      <ArrowRight className="h-3 w-3 text-gray-400" />
                    </Link>
                    
                    {/* 3. Children Safety Nets */}
                    <Link
                      to="/child-safety-net"
                      className="flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-md transition-colors"
                      onClick={() => setIsNetsDropdownOpen(false)}
                    >
                      <span>Children Safety Nets</span>
                      <ArrowRight className="h-3 w-3 text-gray-400" />
                    </Link>
                    
                    {/* 4. Drying Cloth Hangers */}
                    <Link
                      to="/pull-dry-cloth-hangers"
                      className="flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-md transition-colors"
                      onClick={() => setIsNetsDropdownOpen(false)}
                    >
                      <span>Drying Cloth Hangers</span>
                      <ArrowRight className="h-3 w-3 text-gray-400" />
                    </Link>
                    
                    {/* 5. Shade Net */}
                    <Link
                      to="/shade-net"
                      className="flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-md transition-colors"
                      onClick={() => setIsNetsDropdownOpen(false)}
                    >
                      <span>Shade Net</span>
                      <ArrowRight className="h-3 w-3 text-gray-400" />
                    </Link>

                    {/* 6. Balcony Safety Nets */}
                    <Link
                      to="/balcony-safety-nets"
                      className="flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-md transition-colors"
                      onClick={() => setIsNetsDropdownOpen(false)}
                    >
                      <span>Balcony Safety Nets</span>
                      <ArrowRight className="h-3 w-3 text-gray-400" />
                    </Link>
                  </div>
                </motion.div>
              )}
            </div>
            
            <Link to="/about" className="text-gray-600 hover:text-yellow-600 transition-colors">About</Link>

            <div className="relative" ref={moreDropdownRef}>
              <button
                onClick={toggleMoreDropdown}
                className="flex items-center space-x-1 text-gray-600 hover:text-yellow-600 transition-colors"
              >
                <span>More</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${isMoreDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isMoreDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                >

                  <Link
                    to="/faq"
                    className="flex items-center space-x-3 px-4 py-2 text-gray-600 hover:bg-yellow-50 hover:text-yellow-600 transition-colors"
                    onClick={() => setIsMoreDropdownOpen(false)}
                  >
                    <HelpCircle className="h-4 w-4" />
                    <span>FAQ</span>
                  </Link>
                  <Link
                    to="/blog"
                    className="flex items-center space-x-3 px-4 py-2 text-gray-600 hover:bg-yellow-50 hover:text-yellow-600 transition-colors"
                    onClick={() => setIsMoreDropdownOpen(false)}
                  >
                    <BookOpen className="h-4 w-4" />
                    <span>Blog</span>
                  </Link>

                </motion.div>
              )}
            </div>

            <Link to="/contact" className="text-gray-600 hover:text-yellow-600 transition-colors">
              Contact
            </Link>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Safety Consultation Button */}
            <Link
              to="/landing"
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center space-x-2 shadow-lg"
            >
              <Gift className="h-4 w-4" />
              <span>Get Safety Consultation</span>
            </Link>
            
            {/* Get Quote Button */}
            <button
              onClick={() => openWhatsAppQuote()}
              className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 px-6 py-2 rounded-lg font-semibold transition-colors cursor-pointer"
            >
              Get Quote
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-gray-600 hover:text-yellow-600 focus:outline-none focus:text-yellow-600"
            aria-label="Toggle mobile menu"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t border-gray-200 z-50 overflow-hidden"
        >
          <div className="px-4 py-3 space-y-2 bg-white">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1, duration: 0.3 }}
            >
              <Link to="/" className="block px-3 py-2 text-gray-600 hover:bg-yellow-50 hover:text-yellow-600 rounded-md transition-colors" onClick={() => setIsOpen(false)}>Home</Link>
            </motion.div>
            
            {/* Mobile Grills Dropdown */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15, duration: 0.3 }}
            >
              <button
                onClick={toggleMobileGrills}
                className="flex items-center justify-between w-full px-3 py-2 text-gray-600 hover:bg-yellow-50 hover:text-yellow-600 rounded-md transition-colors"
              >
                <span className="flex items-center space-x-2">
                  <Shield className="h-4 w-4" />
                  <span>Grills</span>
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${isMobileGrillsOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isMobileGrillsOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="ml-4 mt-2 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden"
                >
                  <div className="py-1">
                    {/* 1. Balcony Grills */}
                    <Link to="/invisible-grill-balconies" className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 border-b border-gray-100" onClick={() => {setIsOpen(false); setIsMobileGrillsOpen(false);}}>
                      Balcony Grills
                    </Link>
                    
                    {/* 2. Window Grills */}
                    <Link to="/invisible-grill-windows" className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 border-b border-gray-100" onClick={() => {setIsOpen(false); setIsMobileGrillsOpen(false);}}>
                      Window Grills
                    </Link>
                    
                    {/* 3. Apartment Grills */}
                    <Link to="/invisible-grill-apartments" className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 border-b border-gray-100" onClick={() => {setIsOpen(false); setIsMobileGrillsOpen(false);}}>
                      Apartment Grills
                    </Link>
                  </div>
                </motion.div>
              )}
            </motion.div>

            {/* Mobile Nets Dropdown */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.3 }}
            >
              <button
                onClick={toggleMobileNets}
                className="flex items-center justify-between w-full px-3 py-2 text-gray-600 hover:bg-yellow-50 hover:text-yellow-600 rounded-md transition-colors"
              >
                <span className="flex items-center space-x-2">
                  <Grid3X3 className="h-4 w-4" />
                  <span>Nets</span>
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${isMobileNetsOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isMobileNetsOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="ml-4 mt-2 bg-white border border-gray-200 rounded-md shadow-lg max-h-96 overflow-y-auto"
                >
                  <div className="py-1">
                    <Link to="/products" className="block px-4 py-3 text-sm font-medium text-gray-900 hover:bg-gray-50 border-b border-gray-100" onClick={() => {setIsOpen(false); setIsMobileNetsOpen(false);}}>
                      All Products
                    </Link>
                    
                    {/* 1. Anti Bird Net (Pigeon Nets) */}
                    <Link to="/pigeon-nets" className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 border-b border-gray-100" onClick={() => {setIsOpen(false); setIsMobileNetsOpen(false);}}>
                      Anti Bird Net
                    </Link>
                    
                    {/* 2. Cricket Practice Net (Sports Safety) */}
                    <Link to="/sports-safety" className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 border-b border-gray-100" onClick={() => {setIsOpen(false); setIsMobileNetsOpen(false);}}>
                      Cricket Practice Net
                    </Link>
                    
                    {/* 3. Children Safety Nets */}
                    <Link to="/child-safety-net" className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 border-b border-gray-100" onClick={() => {setIsOpen(false); setIsMobileNetsOpen(false);}}>
                      Children Safety Nets
                    </Link>
                    
                    {/* 4. Drying Cloth Hangers */}
                    <Link to="/pull-dry-cloth-hangers" className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 border-b border-gray-100" onClick={() => {setIsOpen(false); setIsMobileNetsOpen(false);}}>
                      Drying Cloth Hangers
                    </Link>
                    
                    {/* 5. Shade Net */}
                    <Link to="/shade-net" className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 border-b border-gray-100" onClick={() => {setIsOpen(false); setIsMobileNetsOpen(false);}}>
                      Shade Net
                    </Link>

                    {/* 6. Balcony Safety Nets */}
                    <Link to="/balcony-safety-nets" className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 border-b border-gray-100" onClick={() => {setIsOpen(false); setIsMobileNetsOpen(false);}}>
                      Balcony Safety Nets
                    </Link>
                  </div>
                </motion.div>
              )}
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.41, duration: 0.3 }}
            >
              <Link to="/about" className="block px-3 py-2 text-gray-600 hover:bg-yellow-50 hover:text-yellow-600 rounded-md transition-colors" onClick={() => setIsOpen(false)}>About</Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.43, duration: 0.3 }}
            >
              <Link to="/faq" className="block px-3 py-2 text-gray-600 hover:bg-yellow-50 hover:text-yellow-600 rounded-md transition-colors" onClick={() => setIsOpen(false)}>FAQ</Link>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.45, duration: 0.3 }}
            >
              <Link to="/blog" className="block px-3 py-2 text-gray-600 hover:bg-yellow-50 hover:text-yellow-600 rounded-md transition-colors" onClick={() => setIsOpen(false)}>Blog</Link>
            </motion.div>

            
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.49, duration: 0.3 }}
            >
              <Link to="/contact" className="block px-3 py-2 text-gray-600 hover:bg-yellow-50 hover:text-yellow-600 rounded-md transition-colors" onClick={() => setIsOpen(false)}>Contact</Link>
            </motion.div>

            {/* Mobile CTA Buttons */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.51, duration: 0.3 }}
              className="pt-4 border-t border-gray-200 space-y-3"
            >
              {/* Mobile Safety Consultation Button */}
              <Link
                to="/landing"
                className="block w-full text-center bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
                onClick={() => setIsOpen(false)}
              >
                <Gift className="h-5 w-5" />
                <span>Get Safety Consultation</span>
              </Link>

              {/* Mobile Get Quote Button */}
              <button
                onClick={() => {
                  openWhatsAppQuote();
                  setIsOpen(false);
                }}
                className="block w-full text-center bg-yellow-500 hover:bg-yellow-600 text-gray-900 px-6 py-3 rounded-lg font-semibold transition-colors cursor-pointer"
              >
                Get Quote
              </button>
            </motion.div>
          </div>
        </motion.div>
      )}
    </nav>
  );
};

export default Navbar;
